import { useState } from "react";
import { X, DeviceMobile, DotsThreeVertical, ArrowSquareOut, PlusSquare, MagnifyingGlass } from "@phosphor-icons/react";
import Logo from "./Logo";
import { useBrand } from "../context/BrandContext";

function ua() {
  return typeof navigator !== "undefined" ? navigator.userAgent : "";
}
export function isIos() {
  return /iPad|iPhone|iPod/.test(ua()) && !window.MSStream;
}
export function isAndroid() {
  return /Android/i.test(ua());
}
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return Boolean(window.navigator && window.navigator.standalone);
}

// iOS share glyph (square with up arrow) — matches the Safari toolbar icon
export const IosShareIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "-3px" }}>
    <path d="M12 3v12" />
    <path d="M8 6.5 12 3l4 3.5" />
    <path d="M7 10H5.5A1.5 1.5 0 0 0 4 11.5v8A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 18.5 10H17" />
  </svg>
);

const Step = ({ n, accent, children }) => (
  <li className="flex gap-3 items-start">
    <span className="shrink-0 h-6 w-6 rounded-full text-[11px] font-bold flex items-center justify-center text-white" style={{ background: accent }}>{n}</span>
    <span>{children}</span>
  </li>
);

export const InstallGuideSheet = ({ open, onClose }) => {
  const brand = useBrand();
  if (!open) return null;
  const name = brand?.brand_name || "Traina";
  const accent = brand?.brand_accent_color || "#2E5BA8";
  const platform = isIos() ? "ios" : isAndroid() ? "android" : "desktop";

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="install-guide">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-sm bg-[#111214] border-t sm:border border-white/15 rounded-t-2xl sm:rounded-2xl p-6 pb-8 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white p-1" aria-label="Close" data-testid="install-guide-close">
          <X size={18} weight="bold" />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="h-12 w-12 rounded-xl border border-white/15 bg-[#141414] flex items-center justify-center overflow-hidden">
            {brand?.brand_logo_b64 ? <img src={brand.brand_logo_b64} alt="" className="h-9 w-9 object-contain" /> : <Logo size={34} />}
          </div>
          <div>
            <div className="font-display text-lg text-white leading-tight">Install {name}</div>
            <div className="text-[11px] text-zinc-500">Takes about 10 seconds</div>
          </div>
        </div>
        {platform === "ios" && (
          <ol className="space-y-4 text-sm text-zinc-300" data-testid="install-guide-ios">
            <Step n={1} accent={accent}>Tap the <span className="font-semibold text-white">Share</span> button <IosShareIcon size={18} color={accent} /> at the bottom of Safari</Step>
            <Step n={2} accent={accent}>Scroll down and tap <span className="font-semibold text-white">Add to Home Screen</span> <PlusSquare size={18} style={{ color: accent, display: "inline", verticalAlign: "-4px" }} /></Step>
            <Step n={3} accent={accent}>Tap <span className="font-semibold text-white">Add</span> — {name} appears on your home screen like any app</Step>
          </ol>
        )}
        {platform === "android" && (
          <ol className="space-y-4 text-sm text-zinc-300" data-testid="install-guide-android">
            <Step n={1} accent={accent}>Tap the <span className="font-semibold text-white">menu</span> button <DotsThreeVertical size={18} weight="bold" style={{ color: accent, display: "inline", verticalAlign: "-4px" }} /> in your browser</Step>
            <Step n={2} accent={accent}>Tap <span className="font-semibold text-white">Add to Home screen</span> or <span className="font-semibold text-white">Install app</span> <ArrowSquareOut size={16} style={{ color: accent, display: "inline", verticalAlign: "-3px" }} /></Step>
            <Step n={3} accent={accent}>Confirm — {name} appears on your home screen like any app</Step>
          </ol>
        )}
        {platform === "desktop" && (
          <ol className="space-y-4 text-sm text-zinc-300" data-testid="install-guide-desktop">
            <Step n={1} accent={accent}><span className="font-semibold text-white">On this computer:</span> look for the <span className="font-semibold text-white">install icon</span> <MagnifyingGlass size={16} style={{ color: accent, display: "inline", verticalAlign: "-3px" }} /> at the right end of your browser's address bar (Chrome/Edge) and click <span className="font-semibold text-white">Install</span></Step>
            <Step n={2} accent={accent}><span className="font-semibold text-white">On your phone:</span> open <span className="font-semibold text-white">{window.location.host}</span> in your phone browser <DeviceMobile size={16} style={{ color: accent, display: "inline", verticalAlign: "-3px" }} /> and a banner will guide you to install</Step>
            <Step n={3} accent={accent}>Once installed, {name} opens full-screen like a native app</Step>
          </ol>
        )}
      </div>
    </div>
  );
};

export const GetAppButton = ({ className = "" }) => {
  const [open, setOpen] = useState(false);
  if (isStandalone()) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-3 px-3 py-2.5 tracker text-zinc-400 hover:bg-[#141414] hover:text-white w-full text-left ${className}`}
        data-testid="get-app-button"
      >
        <DeviceMobile size={18} weight="duotone" /> Get the app
      </button>
      <InstallGuideSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
};
